import { IMAGE, SESSION_STORAGE_REVIEW_KEY, STANDARD_REVIEW_SUBMIT_ERROR, VIDEO } from 'constants/appConstants';
import {
  RECEIVE_ADD_REVIEW_MEDIA_UPLOAD, RECEIVE_SUBMIT_ADD_REVIEW,
  REQUEST_ADD_REVIEW_MEDIA_UPLOAD, REQUEST_SUBMIT_ADD_REVIEW
} from 'constants/reduxActions';

import { authenticationErrorCatchHandlerFactory } from 'actions/errors';
import { setSessionCookies } from 'actions/session';
import { submitMedia as submitMediaApi, submitReview as submitReviewApi } from 'apis/cloudreviews';
import { saveToSessionStorage } from 'helpers/ClientUtils';
import {
  fetchApiNotAuthenticatedMiddleware,
  fetchErrorMiddleware,
  fetchErrorMiddlewareAllowedErrors
} from 'middleware/fetchErrorMiddleware';
import { processHeadersMiddleware } from 'middleware/processHeadersMiddlewareFactory';
import { getMafiaAndCredentials } from 'store/ducks/readFromStore';

export const requestAddReview = () => ({ type: REQUEST_SUBMIT_ADD_REVIEW });

export const requestMediaUpload = () => ({ type: REQUEST_ADD_REVIEW_MEDIA_UPLOAD });

export const receiveAddReview = response => {
  const ret = { type: RECEIVE_SUBMIT_ADD_REVIEW };
  // do not add message directly to object. not all messages are safe to show
  // to customers.
  if (response && response.status === 400 && response.message) {
    const casedMessage = response.message.trim().toLocaleLowerCase();
    if (casedMessage === 'customer is not a premier reviewer') {
      ret.message = 'Premier Reviews are restricted to certain customers.';
    } else {
      ret.message = STANDARD_REVIEW_SUBMIT_ERROR;
    }
  }
  return ret;
};

export const receiveMediaUpload = () => ({ type: RECEIVE_ADD_REVIEW_MEDIA_UPLOAD });

// this is just a helper for `addReview`
const makeAddReviewErrorHandler = args => e => {
  const { dispatch, reviewData, returnTo } = args;
  saveToSessionStorage(SESSION_STORAGE_REVIEW_KEY, JSON.stringify(reviewData));
  authenticationErrorCatchHandlerFactory(dispatch, returnTo)(e);
};

// this is just a helper for `addReview`
const makeAddTextReview = args => payload => {
  const {
    addReviewErrorHandler, credentials, dispatch, mafia,
    submitReviewApi
  } = args;
  return (
    submitReviewApi(mafia.url, credentials, payload)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddlewareAllowedErrors([400]))
      .then(data => dispatch(receiveAddReview(data)))
      .catch(addReviewErrorHandler)
  );
};

// this is just a helper for `addReview`
const uploadMediaAndAddReview = args => {
  const {
    addReviewErrorHandler, addTextReview, cloudreviews, credentials, dispatch,
    reviewData, reviewMedia, submitMediaApi
  } = args;

  dispatch(requestMediaUpload());

  const { images, video } = reviewMedia;
  const { productId } = reviewData;
  const payload = { productId, images };

  if (video) {
    payload.video = video;
  }

  return (
    submitMediaApi(cloudreviews.submitMediaUrl, credentials, payload)
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(resp => {
        dispatch(receiveMediaUpload());
        const images = [];
        let video = null;
        resp.forEach(({ hashValue, mediaType }) => {
          if (mediaType === IMAGE) {
            images.push(hashValue);
          } else if (mediaType === VIDEO) {
            video = hashValue;
          }
        });
        const addTextReviewPayload = { ...reviewData, images, video };
        return addTextReview(addTextReviewPayload);
      })
      .catch(addReviewErrorHandler)
  );
};

export function addReview({ reviewData, reviewMedia = {}, returnTo, submitMedia = submitMediaApi, submitReview = submitReviewApi }) {
  return (dispatch, getState) => {
    dispatch(requestAddReview());

    const { images = [], video } = reviewMedia;
    const hasMediaAttached = images.some(Boolean) || video;

    const state = getState();
    const { environmentConfig: { api: { cloudreviews, mafia } } } = state;
    const subroutineArgs = {
      credentials: getMafiaAndCredentials(state),
      submitMediaApi: submitMedia,
      submitReviewApi: submitReview,
      cloudreviews, dispatch, mafia, returnTo, reviewData, reviewMedia
    };
    subroutineArgs.addReviewErrorHandler = makeAddReviewErrorHandler(subroutineArgs);
    const addTextReview = makeAddTextReview(subroutineArgs);

    if (hasMediaAttached) {
      return uploadMediaAndAddReview({ ...subroutineArgs, addTextReview });
    }
    return addTextReview({ ...reviewData });
  };
}

export function addMediaOnlyReview({ productId, reviewMedia, returnTo, submitMedia = submitMediaApi, submitReview = submitReviewApi }) {
  return (dispatch, getState) => {
    dispatch(requestAddReview());

    const state = getState();
    const { environmentConfig: { api: { cloudreviews, mafia } } } = state;
    const subroutineArgs = {
      reviewData: {
        type: 'zenMediaOnly',
        productId
      },
      credentials: getMafiaAndCredentials(state),
      submitMediaApi: submitMedia,
      submitReviewApi: submitReview,
      cloudreviews, dispatch, mafia, returnTo, reviewMedia
    };
    subroutineArgs.addReviewErrorHandler = makeAddReviewErrorHandler(subroutineArgs);
    const addTextReview = makeAddTextReview(subroutineArgs);

    return uploadMediaAndAddReview({ ...subroutineArgs, addTextReview });
  };
}
